/**
 * @module  public/js/services/errordisplayservice.js
 * @class   ErrorDisplayService
 * @summary Centralised utility for displaying global and inline status banners.
 *
 * @description
 * Provides accessible, type-based banners for info, success, warning and error states.
 * Works with pure DOM manipulation and optional auto-dismiss timers.
 *
 * ## Key Features
 * – Renders a fixed, top-of-viewport global banner.
 * – Creates removable inline banners scoped to any container.
 * – Supports timed auto-dismiss for both banner types.
 * – Exposes helpers to clear global or scoped messages.
 *
 * ## Public API
 * – `global({ text, type, duration })` — show/update a global banner.
 * – `clearGlobal()` — remove the global banner.
 * – `inline({ target, text, type, position, duration })` — inject an inline banner.
 * – `clearInline(target)` — remove all inline banners from a container.
 *
 * ## Dependencies
 * (none)
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

export default class ErrorDisplayService {
  /* ====================================================================
   * GLOBAL  (top-of-page)   ─────────────────────────────────────────── */
  #globalBannerEl = null;

  global({ text, type = 'info', duration = null } = {}) {
    const banner = this.#ensureGlobalBanner();
    banner.className = `ed-banner ed-banner--${type}`;
    banner.textContent = text;

    if (duration) {
      setTimeout(() => this.clearGlobal(), duration);
    }
  }

  clearGlobal() {
    if (this.#globalBannerEl) {
      this.#globalBannerEl.remove();
      this.#globalBannerEl = null;
    }
  }

  #ensureGlobalBanner() {
    if (this.#globalBannerEl) return this.#globalBannerEl;

    const el = document.createElement('div');
    el.className = 'ed-banner ed-banner--info';
    el.setAttribute('role', 'alert');
    el.setAttribute('aria-live', 'assertive');
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.left = '0';
    el.style.right = '0';
    el.style.zIndex = '9999';
    el.style.padding = '10px 16px';
    el.style.fontSize = '14px';
    el.style.fontWeight = '600';
    el.style.textAlign = 'center';
    el.style.boxShadow = '0 2px 4px rgba(0,0,0,.08)';
    document.body.appendChild(el);

    this.#globalBannerEl = el;
    return el;
  }

  /* ====================================================================
   * INLINE (inside any container)  ──────────────────────────────────── */
  /**
   * Shows an inline banner inside the target element (or selector).
   * Returns the DOM element so callers can further customise if needed.
   */
  inline({
    target,
    text,
    type = 'info', // "info" | "success" | "error" | "warning"
    position = 'beforeend', // "afterbegin" | "beforeend" | etc.
    duration = null, // ms ⇒ auto-dismiss
  } = {}) {
    const container =
      typeof target === 'string' ? document.querySelector(target) : target;

    if (!container) {
      // eslint-disable-next-line no-console
      console.warn('[ErrorDisplayService] Target not found:', target);
      return null;
    }

    // Remove existing child banners for cleanliness
    container
      .querySelectorAll(':scope > .ed-inline-banner')
      .forEach((n) => n.remove());

    const banner = document.createElement('div');
    banner.className = `ed-inline-banner ed-banner--${type}`;
    banner.textContent = text;
    banner.style.padding = '8px 12px';
    banner.style.margin = '8px 0';
    banner.style.fontSize = '14px';
    banner.style.borderRadius = '4px';
    banner.style.fontWeight = '500';

    container.insertAdjacentElement(position, banner);

    if (duration) {
      setTimeout(() => banner.remove(), duration);
    }
    return banner;
  }

  /**
   * Convenience wrapper: clears all inline banners inside the target.
   */
  clearInline(target) {
    const container =
      typeof target === 'string' ? document.querySelector(target) : target;

    if (!container) return;
    container
      .querySelectorAll(':scope > .ed-inline-banner')
      .forEach((n) => n.remove());
  }
}

/* ======================================================================
 * Minimal default style – override in your main CSS bundle if desired
 * ==================================================================== */
const styleTag = document.createElement('style');
styleTag.textContent = `
  .ed-banner--info    { background:#e8f4fd; color:#065d93; }
  .ed-banner--success { background:#e6f4ea; color:#067d34; }
  .ed-banner--error   { background:#fdecea; color:#b42318; }
  .ed-banner--warning { background:#fff9e6; color:#b54708; }

  .ed-inline-banner   { border:1px solid transparent; }
  .ed-banner--info.ed-inline-banner    { border-color:#b6e0fe; }
  .ed-banner--success.ed-inline-banner { border-color:#a8e9c1; }
  .ed-banner--error.ed-inline-banner   { border-color:#f8c7c2; }
  .ed-banner--warning.ed-inline-banner { border-color:#ffe58f; }
`;
document.head.appendChild(styleTag);
